import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Bot, CalendarDays, User } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { api } from "../lib/api";
import { BLOG_COVERS } from "../lib/content";

const renderContent = (content) =>
    content.split("\n").map((line, i) => {
        const t = line.trim();
        if (!t) return null;
        if (t.startsWith("## "))
            return (
                <h2 key={i} className="mt-8 font-display text-xl font-bold text-white sm:text-2xl">
                    {t.slice(3)}
                </h2>
            );
        if (/^\d+\.\s/.test(t))
            return (
                <p key={i} className="mt-3 flex gap-3 leading-relaxed text-slate-300">
                    <span className="font-mono2 font-semibold text-cyan-400">{t.match(/^\d+/)[0]}.</span>
                    <span>{t.replace(/^\d+\.\s*/, "").replace(/\*\*/g, "")}</span>
                </p>
            );
        if (t.startsWith("- "))
            return (
                <p key={i} className="mt-3 flex gap-3 leading-relaxed text-slate-300">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-cyan-400" />
                    <span>{t.slice(2).replace(/\*\*/g, "")}</span>
                </p>
            );
        return (
            <p key={i} className="mt-4 leading-relaxed text-slate-300">
                {t.replace(/\*\*/g, "")}
            </p>
        );
    });

export default function BlogPost() {
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        api.get(`/blogs/${slug}`)
            .then((r) => setPost(r.data))
            .catch(() => setError(true));
    }, [slug]);

    if (error)
        return (
            <div className="mx-auto max-w-3xl px-4 py-32 text-center" data-testid="blog-post-not-found">
                <h1 className="font-display text-3xl font-bold text-white">Article not found</h1>
                <Link to="/blog" className="mt-6 inline-flex items-center gap-2 text-cyan-400">
                    <ArrowLeft className="h-4 w-4" /> Back to Blog
                </Link>
            </div>
        );

    if (!post)
        return (
            <div className="py-32 text-center text-slate-500" data-testid="blog-post-loading">
                Loading article…
            </div>
        );

    return (
        <div data-testid="blog-post-page">
            <div className={`relative flex h-64 items-end bg-gradient-to-br sm:h-80 ${BLOG_COVERS[post.cover] || BLOG_COVERS.cyan}`}>
                <Bot className="absolute right-10 top-10 h-20 w-20 text-white/10" />
                <div className="mx-auto w-full max-w-4xl px-4 pb-10 sm:px-6">
                    <span className="rounded-full border border-white/15 bg-black/30 px-3.5 py-1 text-xs font-semibold uppercase tracking-wider text-cyan-300 backdrop-blur">
                        {post.category}
                    </span>
                    <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight text-white sm:text-4xl" data-testid="blog-post-title">
                        {post.title}
                    </h1>
                </div>
            </div>

            <article className="mx-auto max-w-4xl px-4 py-12 sm:px-6">
                <div className="flex flex-wrap items-center gap-5 border-b border-white/10 pb-6 text-xs text-slate-500">
                    <span className="flex items-center gap-1.5"><User className="h-3.5 w-3.5" /> {post.author}</span>
                    <span className="flex items-center gap-1.5">
                        <CalendarDays className="h-3.5 w-3.5" />
                        {new Date(post.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
                    </span>
                </div>
                <div className="pt-6" data-testid="blog-post-content">{renderContent(post.content)}</div>

                {post.related?.length > 0 && (
                    <div className="mt-16">
                        <h2 className="font-display text-xl font-bold text-white">Keep reading</h2>
                        <div className="mt-6 grid gap-5 sm:grid-cols-3">
                            {post.related.map((r) => (
                                <Link key={r.id} to={`/blog/${r.slug}`} data-testid={`related-post-${r.slug}`} className="glow-card group rounded-xl p-5">
                                    <p className="font-display text-sm font-bold leading-snug text-white group-hover:text-cyan-300">{r.title}</p>
                                    <span className="mt-3 flex items-center gap-1 text-xs font-semibold text-cyan-400">
                                        Read <ArrowRight className="h-3 w-3" />
                                    </span>
                                </Link>
                            ))}
                        </div>
                    </div>
                )}

                <Reveal className="mt-16">
                    <div className="glow-card rounded-2xl p-8 text-center">
                        <h2 className="font-display text-xl font-bold text-white">
                            Want this working for <span className="cyan-gradient-text">your business?</span>
                        </h2>
                        <Link
                            to="/contact"
                            data-testid="blog-post-cta"
                            className="mt-5 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-600 to-cyan-500 px-7 py-3 text-sm font-semibold text-white"
                        >
                            Talk to Us <ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>
                </Reveal>
            </article>
        </div>
    );
}
